/**
 * Aba "Plano original" — mostra a baseline capturada quando o projeto saiu
 * de planejado e a compara com o estado atual.
 *
 * Empty state quando ainda nao iniciou: explica quando a baseline e capturada.
 */

import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Camera, TrendingUp, TrendingDown, Minus, AlertTriangle, Plus, X } from 'lucide-react';
import { projetosAPI } from '../../../services/inovacaoAPI';
import { mensagemErro } from '../labels';

export default function BaselineWS({ projeto }) {
  const [comp, setComp] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    projetosAPI.baselineComparacao(projeto.id)
      .then(setComp)
      .catch((e) => toast.error(mensagemErro(e, 'Erro ao carregar baseline')))
      .finally(() => setLoading(false));
  }, [projeto.id]);

  if (loading) return <p style={{ color: '#7f8c8d' }}>Carregando…</p>;

  if (!comp || !comp.tem_baseline) {
    return (
      <div style={{ background: '#fff', padding: 40, borderRadius: 12, textAlign: 'center',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
        <Camera size={32} color="#bdc3c7" style={{ marginBottom: 12 }} />
        <h3 style={{ margin: 0, color: '#7f8c8d' }}>Sem plano original ainda</h3>
        <p style={{ color: '#95a5a6', marginTop: 8, maxWidth: 480, marginLeft: 'auto', marginRight: 'auto' }}>
          A baseline é congelada quando você inicia a primeira fase. Até lá o plano ainda está sendo desenhado, então ajustar datas, horas ou escopo não exige comparação.
        </p>
      </div>
    );
  }

  const horasDelta = (parseFloat(comp.horas_estimadas_atual) - parseFloat(comp.horas_estimadas_baseline));
  const horasReal = parseFloat(comp.horas_realizadas);
  return (
    <div>
      <div style={{ background: '#fff', padding: 20, borderRadius: 10, marginBottom: 16,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <Camera size={18} color="var(--brand, #20a490)" />
          <h3 style={{ margin: 0, color: '#2c3e50' }}>Plano original (capturado em {formatarData(comp.capturado_em)})</h3>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          <MetricaDelta label="Data fim planejada"
                        baseline={comp.data_fim_baseline} atual={comp.data_fim_atual}
                        delta={comp.delta_dias_fim} unidade="dia" />
          <MetricaDelta label="Data fim REAL"
                        baseline={comp.data_fim_baseline} atual={comp.data_fim_real}
                        delta={comp.delta_dias_real} unidade="dia"
                        destacar={comp.data_fim_real} />
          <MetricaHoras label="Horas estimadas (nao-buffer)"
                        baseline={comp.horas_estimadas_baseline} atual={comp.horas_estimadas_atual}
                        delta={horasDelta} />
          <MetricaHoras label="Horas realizadas vs plano"
                        baseline={comp.horas_estimadas_baseline} atual={comp.horas_realizadas}
                        delta={horasReal - parseFloat(comp.horas_estimadas_baseline)}
                        destacar={comp.horas_realizadas > 0} />
        </div>

        {(comp.fases_adicionadas > 0 || comp.fases_removidas > 0) && (
          <div style={{ marginTop: 16, padding: 12, background: '#fffaf0', borderRadius: 6,
                        border: '1px solid #ffecc4', fontSize: '0.85rem' }}>
            <AlertTriangle size={14} color="#ff7a00" style={{ verticalAlign: 'middle', marginRight: 6 }} />
            <b style={{ color: '#ff7a00' }}>Mudancas de escopo:</b>{' '}
            {comp.fases_adicionadas > 0 && <>{comp.fases_adicionadas} fase(s) adicionada(s)</>}
            {comp.fases_adicionadas > 0 && comp.fases_removidas > 0 && ', '}
            {comp.fases_removidas > 0 && <>{comp.fases_removidas} removida(s)</>}.
          </div>
        )}
      </div>

      <h4 style={{ margin: '0 0 10px', color: '#2c3e50', fontSize: '0.95rem' }}>Comparacao fase a fase</h4>
      <div style={{ background: '#fff', borderRadius: 10, overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
          <thead style={{ background: '#f8f9fa' }}>
            <tr>
              <th style={th}>Fase</th>
              <th style={th}>Status</th>
              <th style={th}>Horas plano → atual (Δ)</th>
              <th style={th}>Horas realizadas</th>
              <th style={th}>Data fim plano → atual (Δ dias)</th>
            </tr>
          </thead>
          <tbody>
            {comp.fases.map((f) => <LinhaFase key={f.fase_id} f={f} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LinhaFase({ f }) {
  let badge, badgeCor;
  if (f.presente_no_baseline && !f.presente_no_atual) { badge = 'removida'; badgeCor = '#e74c3c'; }
  else if (!f.presente_no_baseline && f.presente_no_atual) { badge = 'adicionada'; badgeCor = '#ff7a00'; }
  else { badge = 'no plano'; badgeCor = '#7f8c8d'; }

  return (
    <tr style={{ borderTop: '1px solid #ecf0f1' }}>
      <td style={td}>{f.nome}</td>
      <td style={td}>
        <span style={{ background: badgeCor, color: '#fff', padding: '2px 8px',
                       borderRadius: 10, fontSize: '0.72rem' }}>{badge}</span>
      </td>
      <td style={td}>
        {fmtNum(f.horas_baseline)} → {fmtNum(f.horas_atual)}
        {f.delta_horas !== null && f.delta_horas !== undefined && (
          <SetaDelta valor={parseFloat(f.delta_horas)} suffix="h" />
        )}
      </td>
      <td style={td}>{fmtNum(f.horas_realizadas)}h</td>
      <td style={td}>
        {fmtData(f.data_fim_baseline)} → {fmtData(f.data_fim_atual)}
        {f.delta_dias_fim !== null && f.delta_dias_fim !== undefined && (
          <SetaDelta valor={f.delta_dias_fim} suffix="d" />
        )}
      </td>
    </tr>
  );
}

function MetricaDelta({ label, baseline, atual, delta, unidade, destacar }) {
  return (
    <div style={{ background: destacar ? '#fff8e1' : '#f8f9fa', padding: 12, borderRadius: 6 }}>
      <div style={{ fontSize: '0.72rem', color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: 0.3 }}>
        {label}
      </div>
      <div style={{ fontSize: '0.9rem', color: '#2c3e50', marginTop: 4 }}>
        {fmtData(baseline)} → <b>{fmtData(atual)}</b>
      </div>
      {delta !== null && delta !== undefined && (
        <SetaDelta valor={delta} suffix={unidade.charAt(0)} />
      )}
    </div>
  );
}

function MetricaHoras({ label, baseline, atual, delta, destacar }) {
  return (
    <div style={{ background: destacar ? '#fff8e1' : '#f8f9fa', padding: 12, borderRadius: 6 }}>
      <div style={{ fontSize: '0.72rem', color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: 0.3 }}>
        {label}
      </div>
      <div style={{ fontSize: '0.9rem', color: '#2c3e50', marginTop: 4 }}>
        {fmtNum(baseline)}h → <b>{fmtNum(atual)}h</b>
      </div>
      {delta !== null && delta !== undefined && (
        <SetaDelta valor={delta} suffix="h" />
      )}
    </div>
  );
}

function SetaDelta({ valor, suffix }) {
  if (valor === 0 || valor === null || valor === undefined) {
    return (
      <span style={{ marginLeft: 8, color: '#7f8c8d', fontSize: '0.8rem',
                     display: 'inline-flex', alignItems: 'center', gap: 2 }}>
        <Minus size={12} /> 0{suffix}
      </span>
    );
  }
  const positivo = valor > 0;
  const Icon = positivo ? TrendingUp : TrendingDown;
  const cor = positivo ? '#e74c3c' : '#27ae60';
  return (
    <span style={{ marginLeft: 8, color: cor, fontSize: '0.8rem', fontWeight: 600,
                   display: 'inline-flex', alignItems: 'center', gap: 2 }}>
      <Icon size={12} /> {positivo ? '+' : ''}{valor}{suffix}
    </span>
  );
}

function fmtNum(v) {
  if (v === null || v === undefined) return '—';
  const n = parseFloat(v);
  return isNaN(n) ? '—' : n.toFixed(1).replace(/\.0$/, '');
}

function fmtData(iso) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y.slice(2)}`;
}

function formatarData(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

const th = { textAlign: 'left', padding: '10px 12px', fontSize: '0.72rem',
             color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: 0.3, fontWeight: 600 };
const td = { padding: '10px 12px', verticalAlign: 'middle' };
